import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { deleteOAuthClient, listOAuthClients, upsertOAuthClient } from './api';

// Vendor catalog known up-front. Used to render the right docs links;
// extending this is just adding a row.
const VENDOR_DOCS: Record<string, { name: string; setupURL: string }> = {
  google: {
    name: 'Google',
    setupURL: 'https://console.cloud.google.com/apis/credentials',
  },
  microsoft: {
    name: 'Microsoft',
    setupURL: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps',
  },
  slack: {
    name: 'Slack',
    setupURL: 'https://api.slack.com/apps',
  },
};

export function OAuthCredentialsSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const clients = useQuery({
    queryKey: ['oauth-clients', orgId],
    queryFn: () => listOAuthClients(orgId),
    enabled: !!orgId,
  });

  return (
    <Card>
      <CardHeader
        title="OAuth credentials"
        description="One-time setup per vendor. Sigil uses these credentials to negotiate tokens for the connectors below. The secret is encrypted with KMS; we never log it."
      />
      <CardBody className="space-y-4">
        <RedirectURIBox />

        {clients.data && clients.data.length > 0 && (
          <ul className="space-y-2">
            {clients.data.map((c) => (
              <ClientRow
                key={c.id}
                vendor={c.vendor}
                clientId={c.client_id}
                onRemove={async () => {
                  await deleteOAuthClient(orgId, c.vendor);
                  qc.invalidateQueries({ queryKey: ['oauth-clients', orgId] });
                }}
              />
            ))}
          </ul>
        )}

        {open ? (
          <OAuthClientForm
            orgId={orgId}
            onCancel={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              qc.invalidateQueries({ queryKey: ['oauth-clients', orgId] });
            }}
          />
        ) : (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            + Add OAuth credentials
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

function RedirectURIBox() {
  const url = `${window.location.protocol}//${window.location.hostname.replace('agents.', 'api.')}/v1/connectors/oauth/callback`;
  return (
    <div className="rounded-lg border border-line-soft bg-surface-soft/40 px-3 py-2">
      <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
        Add this redirect URI to your OAuth client
      </p>
      <code className="mt-1 block break-all font-mono text-xs text-ink-primary">{url}</code>
    </div>
  );
}

function ClientRow({
  vendor,
  clientId,
  onRemove,
}: {
  vendor: string;
  clientId: string;
  onRemove: () => void;
}) {
  const meta = VENDOR_DOCS[vendor];
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-surface-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink-primary">{meta?.name ?? vendor}</p>
        <p className="font-mono text-[11px] text-ink-tertiary">{clientId}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          if (window.confirm(`Remove ${vendor} OAuth credentials? Active connections will break.`)) {
            onRemove();
          }
        }}
        className="text-xs text-danger hover:underline"
      >
        Remove
      </button>
    </li>
  );
}

function OAuthClientForm({
  orgId,
  onCancel,
  onSaved,
}: {
  orgId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [vendor, setVendor] = useState('google');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const save = useMutation({
    mutationFn: () =>
      upsertOAuthClient(orgId, { vendor, client_id: clientId, client_secret: clientSecret }),
    onSuccess: onSaved,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!clientId.trim() || !clientSecret.trim()) return;
    save.mutate();
  }

  const meta = VENDOR_DOCS[vendor];
  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-line bg-surface-soft/40 p-4">
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Vendor</label>
        <select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary"
        >
          {Object.entries(VENDOR_DOCS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.name}
            </option>
          ))}
        </select>
        {meta && (
          <p className="mt-1 text-xs text-ink-tertiary">
            Create credentials in the{' '}
            <a href={meta.setupURL} target="_blank" rel="noreferrer" className="text-bit2me hover:underline">
              {meta.name} console ↗
            </a>
          </p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Client ID</label>
        <input
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 font-mono text-xs text-ink-primary"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Client secret</label>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 font-mono text-xs text-ink-primary"
          required
        />
        <p className="mt-1 text-xs text-ink-tertiary">
          Encrypted with KMS at rest. We never log it.
        </p>
      </div>
      {save.error && (
        <p className="text-xs text-danger">{(save.error as Error).message || 'Could not save'}</p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
